import React, { useState, useEffect, useRef } from 'react';
import { useDispatch } from 'react-redux';
import { useParams, useSearchParams, useNavigate, useLocation } from 'react-router-dom';
import styles from './AddCustomerToUnit.module.css';
import { searchCustomers, fetchCustomerDetails } from '../services/customerService';
import { addUnitCustomer, fetchUnitDetails, fetchCustomerUnitData, updateCustomerUnitDataEdit } from '../services/unitService';
import { showSuccessNotification, showErrorNotification } from '../store/slices/notificationSlice';

const AddCustomerToUnit = ({ unit: propUnit, onSave, onCancel }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const location = useLocation();
    const { unitId } = useParams();
    const [searchParams] = useSearchParams();

    // Check if we're in edit mode based on URL path
    const isInEditMode = location.pathname.includes('update-installments');

    // State for unit data (can come from props or be fetched)
    const [unit, setUnit] = useState(propUnit);
    const [unitLoading, setUnitLoading] = useState(!propUnit && !!unitId);

    // Pre-populate customer if coming from AddUnitToCustomer
    const preSelectedCustomerId = searchParams.get('customerId');
    const preSelectedCustomerName = searchParams.get('customerName');
    const preSelectedUnitPrice = searchParams.get('unitPrice');
    const preSelectedUnitLabel = searchParams.get('unitLabel');
    const preSelectedCustomerSwagId = searchParams.get('customerSwagId');

    const [customerFromEdit, setCustomerFromEdit] = useState(null);
    const [selectedCustomer, setSelectedCustomer] = useState(preSelectedCustomerId || '');
    const [selectedCustomerData, setSelectedCustomerData] = useState(null);
    const [paymentMethod, setPaymentMethod] = useState('installment');
    const [totalPrice, setTotalPrice] = useState(() => {
        if (preSelectedUnitPrice) return parseFloat(preSelectedUnitPrice);
        return propUnit?.price || 250000;
    });
    const [clientInterests, setClientInterests] = useState('Residential');
    const [purposeOfPurchase, setPurposeOfPurchase] = useState('Investment');
    const [downPayment, setDownPayment] = useState('');
    const [downPaymentConversion, setDownPaymentConversion] = useState('');
    const [maintenanceValue, setMaintenanceValue] = useState('');
    const [maintenanceEntries, setMaintenanceEntries] = useState([
        { id: 1, value: '', date: '', paid: false }
    ]);
    const [utilities, setUtilities] = useState('');
    const [utilitiesEntries, setUtilitiesEntries] = useState([
        { id: 1, value: '', date: '', paid: false }
    ]);
    const [currency, setCurrency] = useState('EGP');
    const [customTotalPrice, setCustomTotalPrice] = useState('');
    const [contractDate, setContractDate] = useState('');
    const [isEditMode, setIsEditMode] = useState(false);
    const [unitCustomerId, setUnitCustomerId] = useState('');
    const [clientComments, setClientComments] = useState('');
    const [searchQuery, setSearchQuery] = useState('');
    const [isDropdownOpen, setIsDropdownOpen] = useState(false);
    const [customers, setCustomers] = useState([]);
    const [loading, setLoading] = useState(false);
    const [saveLoading, setSaveLoading] = useState(false);
    const [editLoading, setEditLoading] = useState(false);
    const [saveError, setSaveError] = useState('');
    const dropdownRef = useRef(null);
    const [installments, setInstallments] = useState([
        { id: 1, amount: '', date: '', amount_conversion: '', status: 'unpaid' },
        { id: 2, amount: '', date: '', amount_conversion: '', status: 'unpaid' },
        { id: 3, amount: '', date: '', amount_conversion: '', status: 'unpaid' }
    ]);
    const [installmentYear, setInstallmentYear] = useState('');
    const [installmentAmountError, setInstallmentAmountError] = useState('');

    // Display states for formatted values
    const [downPaymentDisplay, setDownPaymentDisplay] = useState('');
    const [downPaymentConversionDisplay, setDownPaymentConversionDisplay] = useState('');
    const [maintenanceValueDisplay, setMaintenanceValueDisplay] = useState('');
    const [utilitiesDisplay, setUtilitiesDisplay] = useState('');
    const [customTotalPriceDisplay, setCustomTotalPriceDisplay] = useState('');

    // Format number with commas for display (max 2 decimal places)
    const formatNumber = (num) => {
        if (!num || num === '') return '';
        const cleanedNum = num.toString().replace(/,/g, '');
        const number = parseFloat(cleanedNum);

        if (isNaN(number)) return '';

        // Handle decimal numbers properly
        const parts = cleanedNum.split('.');
        const integerPart = parseInt(parts[0], 10);
        const decimalPart = parts[1];

        let formattedInteger = integerPart.toLocaleString();

        // If there's a decimal part, add it back
        if (decimalPart !== undefined) {
            return `${formattedInteger}.${decimalPart}`;
        }

        return formattedInteger;
    };

    // Remove formatting to get raw number
    const parseFormattedNumber = (formattedStr) => {
        if (!formattedStr) return 0;
        const cleanedStr = formattedStr.toString().replace(/,/g, '');
        const number = parseFloat(cleanedStr);
        return isNaN(number) ? 0 : number;
    };

    // Convert ISO date to yyyy-MM-dd format for HTML date inputs
    const formatDateForInput = (dateString) => {
        if (!dateString) return '';
        try {
            const date = new Date(dateString);
            return date.toISOString().split('T')[0];
        } catch (error) {
            console.error('Error formatting date:', error);
            return '';
        }
    };

    // Helper functions for maintenance and utilities entries
    const addMaintenanceEntry = () => {
        const newId = Math.max(...maintenanceEntries.map(entry => entry.id)) + 1;
        setMaintenanceEntries(prev => [...prev, { id: newId, value: '', date: '', paid: false }]);
    };

    const deleteMaintenanceEntry = (id) => {
        if (maintenanceEntries.length > 1) {
            setMaintenanceEntries(prev => prev.filter(entry => entry.id !== id));
        }
    };

    const handleMaintenanceEntryChange = (id, field, value) => {
        if (field === 'value') {
            // Allow empty string
            if (value === '') {
                setMaintenanceEntries(prev => {
                    const updated = prev.map(entry =>
                        entry.id === id ? { ...entry, [field]: '' } : entry
                    );
                    return updated;
                });
                return;
            }

            // Remove commas for processing
            const cleanedValue = value.toString().replace(/,/g, '');

            // Validate decimal input (max 2 decimal places)
            const decimalRegex = /^\d+(\.\d{0,2})?$/;
            if (decimalRegex.test(cleanedValue)) {
                const numValue = parseFloat(cleanedValue);

                // Allow positive numbers (including decimals)
                if (numValue >= 0 && !isNaN(numValue)) {
                    setMaintenanceEntries(prev => {
                        const updated = prev.map(entry =>
                            entry.id === id ? { ...entry, [field]: cleanedValue } : entry
                        );
                        return updated;
                    });
                }
            }
        } else {
            // Handle non-numeric fields (like date and paid)
            setMaintenanceEntries(prev => {
                const updated = prev.map(entry =>
                    entry.id === id ? { ...entry, [field]: value } : entry
                );
                return updated;
            });
        }
    };

    const addUtilitiesEntry = () => {
        const newId = Math.max(...utilitiesEntries.map(entry => entry.id)) + 1;
        setUtilitiesEntries(prev => [...prev, { id: newId, value: '', date: '', paid: false }]);
    };

    const deleteUtilitiesEntry = (id) => {
        if (utilitiesEntries.length > 1) {
            setUtilitiesEntries(prev => prev.filter(entry => entry.id !== id));
        }
    };

    const handleUtilitiesEntryChange = (id, field, value) => {
        if (field === 'value') {
            // Allow empty string
            if (value === '') {
                setUtilitiesEntries(prev => {
                    const updated = prev.map(entry =>
                        entry.id === id ? { ...entry, [field]: '' } : entry
                    );
                    return updated;
                });
                return;
            }

            // Remove commas for processing
            const cleanedValue = value.toString().replace(/,/g, '');

            // Validate decimal input (max 2 decimal places)
            const decimalRegex = /^\d+(\.\d{0,2})?$/;
            if (decimalRegex.test(cleanedValue)) {
                const numValue = parseFloat(cleanedValue);

                // Allow positive numbers (including decimals)
                if (numValue >= 0 && !isNaN(numValue)) {
                    setUtilitiesEntries(prev => {
                        const updated = prev.map(entry =>
                            entry.id === id ? { ...entry, [field]: cleanedValue } : entry
                        );
                        return updated;
                    });
                }
            }
        } else {
            // Handle non-numeric fields (like date and paid)
            setUtilitiesEntries(prev => {
                const updated = prev.map(entry =>
                    entry.id === id ? { ...entry, [field]: value } : entry
                );
                return updated;
            });
        }
    };



    // Handle formatted numeric input
    const handleFormattedNumericChange = (field, value, displaySetter) => {
        // Allow empty string
        if (value === '') {
            if (field === 'downPayment') setDownPayment('');
            else if (field === 'downPaymentConversion') setDownPaymentConversion('');
            else if (field === 'maintenanceValue') setMaintenanceValue('');
            else if (field === 'utilities') setUtilities('');
            else if (field === 'customTotalPrice') setCustomTotalPrice('');

            displaySetter('');
            return;
        }

        // Remove commas for processing
        const cleanedValue = value.toString().replace(/,/g, '');

        // Validate decimal input (max 2 decimal places)
        const decimalRegex = /^\d+(\.\d{0,2})?$/;
        if (decimalRegex.test(cleanedValue)) {
            const numValue = parseFloat(cleanedValue);

            // Allow positive numbers (including decimals)
            if (numValue >= 0 && !isNaN(numValue)) {
                if (field === 'downPayment') {
                    setDownPayment(cleanedValue);
                    setTimeout(() => validateInstallmentAmounts(true), 0);
                } else if (field === 'downPaymentConversion') {
                    setDownPaymentConversion(cleanedValue);
                    setTimeout(() => validateInstallmentAmounts(true), 0);
                } else if (field === 'maintenanceValue') {
                    setMaintenanceValue(cleanedValue);
                } else if (field === 'utilities') {
                    setUtilities(cleanedValue);
                } else if (field === 'customTotalPrice') {
                    setCustomTotalPrice(cleanedValue);
                    setTimeout(() => validateInstallmentAmounts(true), 0);
                }

                // Update display value with formatting
                displaySetter(formatNumber(cleanedValue));
            }
        }
    };

    // Search customers when search query changes
    useEffect(() => {
        const searchCustomersAPI = async () => {
            if (searchQuery.trim().length > 0) {
                setLoading(true);
                try {
                    const response = await searchCustomers(searchQuery);
                    if (response.data && Array.isArray(response.data)) {
                        console.log('Search results:', response.data);
                        setCustomers(response.data);
                    } else {
                        setCustomers([]);
                    }
                } catch (error) {
                    console.error('Failed to search customers:', error);
                    setCustomers([]);
                } finally {
                    setLoading(false);
                }
            } else {
                setCustomers([]);
            }
        };

        // Debounce the search to avoid too many API calls
        const timeoutId = setTimeout(searchCustomersAPI, 300);
        return () => clearTimeout(timeoutId);
    }, [searchQuery]);

    // Handle click outside dropdown
    useEffect(() => {
        const handleClickOutside = (event) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target)) {
                setIsDropdownOpen(false);
            }
        };

        if (isDropdownOpen) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isDropdownOpen]);

    // Fetch unit data if unitId is provided in URL
    useEffect(() => {
        if (unitId && !propUnit) {
            const fetchUnit = async () => {
                setUnitLoading(true);
                try {
                    const response = await fetchUnitDetails(unitId);
                    setUnit(response.data || response);
                    if (response.data?.price || response.price) {
                        setTotalPrice(response.data?.price || response.price);
                    }
                } catch (error) {
                    console.error('Failed to fetch unit details:', error);
                    dispatch(showErrorNotification(`Failed to load unit details: ${error.message}`));
                } finally {
                    setUnitLoading(false);
                }
            };
            fetchUnit();
        }
    }, [unitId, propUnit, dispatch]);

    // Pre-populate customer data if customerId is provided in URL
    useEffect(() => {
        if (preSelectedCustomerId && preSelectedCustomerName) {
            const fetchCustomer = async () => {
                try {
                    const response = await fetchCustomerDetails(preSelectedCustomerId);
                    const customerData = response.data || response;
                    // Ensure swag_id is included, use URL parameter as fallback
                    setSelectedCustomerData({
                        ...customerData,
                        swag_id: customerData.swag_id || preSelectedCustomerSwagId
                    });
                } catch (error) {
                    console.error('Failed to fetch customer details:', error);
                    // Fall back to just using the data from URL parameters
                    setSelectedCustomerData({
                        _id: preSelectedCustomerId,
                        name: preSelectedCustomerName,
                        swag_id: preSelectedCustomerSwagId
                    });
                }
            };
            fetchCustomer();
        }
    }, [preSelectedCustomerId, preSelectedCustomerName, preSelectedCustomerSwagId]);

    // Update maintenance and utilities totals when entries change
    useEffect(() => {
        const total = maintenanceEntries.reduce((sum, entry) => {
            const value = parseFormattedNumber(entry.value) || 0;
            return sum + value;
        }, 0);
        setMaintenanceValue(total.toString());
        setMaintenanceValueDisplay(formatNumber(total.toString()));
        setTimeout(() => validateInstallmentAmounts(true), 0);
    }, [maintenanceEntries]);

    useEffect(() => {
        const total = utilitiesEntries.reduce((sum, entry) => {
            const value = parseFormattedNumber(entry.value) || 0;
            return sum + value;
        }, 0);
        setUtilities(total.toString());
        setUtilitiesDisplay(formatNumber(total.toString()));
        setTimeout(() => validateInstallmentAmounts(true), 0);
    }, [utilitiesEntries]);

    // Load existing customer unit data when in edit mode
    useEffect(() => {
        if (isInEditMode && unitId) {
            const loadCustomerUnitData = async () => {
                try {
                    setUnitLoading(true);
                    const response = await fetchCustomerUnitData(unitId);
                    const data = response.data?.data || response.data;

                    console.log(`data is ${data}`);

                    if (data) {
                        // Set form data from API response
                        setCustomerFromEdit(data.user_name || data.customer_name || '');
                        setUnitCustomerId(data.unit_customer_id || '');
                        setSelectedCustomer(data.unit_customer_id || '');
                        setPaymentMethod(data.payment_method || 'cash');
                        setClientInterests(data.client_interests || 'Residential');
                        setPurposeOfPurchase(data.purpose_of_purchase || 'Investment');
                        setClientComments(data.client_comments || '');
                        setCurrency(data.currency || 'EGP');
                        setTotalPrice(data.unit_price || 250000);

                        // maintenance and utilities are arrays — totals computed by useEffect after entries are set

                        // Load maintenance entries if available
                        if (data.maintenance && Array.isArray(data.maintenance)) {
                            const formattedMaintenanceEntries = data.maintenance.map((entry, index) => ({
                                id: index + 1,
                                value: entry.amount?.toString() || '',
                                date: formatDateForInput(entry.date) || '',
                                paid: Boolean(entry.paid)
                            }));
                            setMaintenanceEntries(formattedMaintenanceEntries.length > 0 ? formattedMaintenanceEntries : [{ id: 1, value: '', date: '', paid: false }]);
                        }

                        // Load utilities entries if available
                        if (data.utilities && Array.isArray(data.utilities)) {
                            const formattedUtilitiesEntries = data.utilities.map((entry, index) => ({
                                id: index + 1,
                                value: entry.amount?.toString() || '',
                                date: formatDateForInput(entry.date) || '',
                                paid: Boolean(entry.paid)
                            }));
                            setUtilitiesEntries(formattedUtilitiesEntries.length > 0 ? formattedUtilitiesEntries : [{ id: 1, value: '', date: '', paid: false }]);
                        }

                        // Set custom total price if not EGP
                        if (data.currency !== 'EGP' && data.total_price) {
                            setCustomTotalPrice(data.total_price.toString());
                            setCustomTotalPriceDisplay(data.total_price.toLocaleString());
                        }

                        // Set down payments
                        if (data.down_payment) {
                            setDownPayment(data.down_payment.toString());
                            setDownPaymentDisplay(data.down_payment.toLocaleString());
                        }
                        if (data.down_payment_conversion) {
                            setDownPaymentConversion(data.down_payment_conversion.toString());
                            setDownPaymentConversionDisplay(data.down_payment_conversion.toLocaleString());
                        }

                        // Set contract date - Fix timezone issue
                        if (data.contract_date) {
                            const contractDateObj = new Date(data.contract_date);
                            // Use local date components to avoid timezone shifts
                            const year = contractDateObj.getFullYear();
                            const month = String(contractDateObj.getMonth() + 1).padStart(2, '0');
                            const day = String(contractDateObj.getDate()).padStart(2, '0');
                            setContractDate(`${year}-${month}-${day}`);
                        }

                        // Set installment year
                        setInstallmentYear(data.installment_year?.toString() || '');

                        if (data.installments && Array.isArray(data.installments) && data.installments.length > 0) {
                            const formattedInstallments = data.installments.map((inst, index) => {
                                let formattedDate = '';
                                if (inst.date) {
                                    const dateObj = new Date(inst.date);
                                    const year = dateObj.getFullYear();
                                    const month = String(dateObj.getMonth() + 1).padStart(2, '0');
                                    const day = String(dateObj.getDate()).padStart(2, '0');
                                    formattedDate = `${year}-${month}-${day}`;
                                }
                                return {
                                    id: index + 1,
                                    amount: inst.amount?.toString() || '',
                                    amount_conversion: inst.amount_conversion?.toString() || '',
                                    date: formattedDate,
                                    status: inst.status || 'unpaid'
                                };
                            });
                            setInstallments(formattedInstallments);
                        }

                        setIsEditMode(true);
                    }
                } catch (error) {
                    console.error('Failed to load customer unit data:', error);
                    dispatch(showErrorNotification(`Failed to load data: ${error.message}`));
                } finally {
                    setUnitLoading(false);
                }
            };

            loadCustomerUnitData();
        }
    }, [isInEditMode, unitId, dispatch]);

    const handleCustomerSelect = (customerSwagId,name) => {
        const selectedCustomer = customers.find(c => c.swag_id === customerSwagId && c.name === name);
        console.log('Selected customer:', selectedCustomer);
        setSelectedCustomer(customerSwagId);
        setSelectedCustomerData(selectedCustomer);
        setIsDropdownOpen(false);
        setSearchQuery('');
        // Clear the customers list after selection to prevent default selection
        setCustomers([]);
    };

    const handleInstallmentChange = (id, field, value) => {
        if (field === 'amount' || field === 'amount_conversion') {
            // Allow empty string
            if (value === '') {
                setInstallments(prev => {
                    const updated = prev.map(inst =>
                        inst.id === id ? { ...inst, [field]: '' } : inst
                    );
                    setTimeout(() => validateInstallmentAmounts(true), 0);
                    return updated;
                });
                return;
            }

            // Remove commas for processing
            const cleanedValue = value.toString().replace(/,/g, '');

            // Validate decimal input (max 2 decimal places)
            const decimalRegex = /^\d+(\.\d{0,2})?$/;
            if (decimalRegex.test(cleanedValue)) {
                const numValue = parseFloat(cleanedValue);

                // Allow positive numbers (including decimals)
                if (numValue >= 0 && !isNaN(numValue)) {
                    setInstallments(prev => {
                        const updated = prev.map(inst =>
                            inst.id === id ? { ...inst, [field]: cleanedValue } : inst
                        );
                        setTimeout(() => validateInstallmentAmounts(true), 0);
                        return updated;
                    });
                }
            }
        } else {
            // Handle non-numeric fields (like date)
            setInstallments(prev => {
                const updated = prev.map(inst =>
                    inst.id === id ? { ...inst, [field]: value } : inst
                );

                // Validate amounts after updating if the changed field is amount or amount_conversion
                if (field === 'amount' || field === 'amount_conversion') {
                    setTimeout(() => validateInstallmentAmounts(true), 0);
                }

                return updated;
            });
        }
    };

    const handleAmountKeyPress = (e) => {
        // Allow clipboard operations (Ctrl+C, Ctrl+V, Ctrl+X, Ctrl+A, Ctrl+Z)
        if (e.ctrlKey || e.metaKey) {
            return; // Allow all Ctrl/Cmd operations
        }

        // Allow only numbers, backspace, delete, arrow keys, and decimal point
        const allowedKeys = ['0', '1', '2', '3', '4', '5', '6', '7', '8', '9', '.', 'Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown', 'Tab', 'Home', 'End'];
        if (!allowedKeys.includes(e.key)) {
            e.preventDefault();
        }
        // Prevent multiple decimal points
        if (e.key === '.' && e.target.value.includes('.')) {
            e.preventDefault();
        }
        // Prevent negative sign
        if (e.key === '-') {
            e.preventDefault();
        }
        // Prevent more than 2 decimal places
        if (['0', '1', '2', '3', '4', '5', '6', '7', '8', '9'].includes(e.key)) {
            const currentValue = e.target.value;
            const decimalIndex = currentValue.indexOf('.');
            if (decimalIndex !== -1) {
                const afterDecimal = currentValue.substring(decimalIndex + 1);
                const cursorPosition = e.target.selectionStart;
                // If cursor is after decimal and we already have 2 digits, prevent input
                if (cursorPosition > decimalIndex && afterDecimal.length >= 2) {
                    e.preventDefault();
                }
            }
        }
    };

    const handleAmountPaste = (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text/plain');

        // Clean the pasted data - remove any non-numeric characters except decimal point
        let cleanedData = pastedData.replace(/[^0-9.]/g, '');

        // Ensure only one decimal point
        const decimalParts = cleanedData.split('.');
        if (decimalParts.length > 2) {
            cleanedData = decimalParts[0] + '.' + decimalParts.slice(1).join('');
        }

        // Limit decimal places to 2
        if (cleanedData.includes('.')) {
            const [integerPart, decimalPart] = cleanedData.split('.');
            cleanedData = integerPart + '.' + decimalPart.substring(0, 2);
        }

        // Skip if no valid data
        if (!cleanedData || cleanedData === '.') return;

        const currentTarget = e.target;

        // Create a formatted version with commas for display
        const formattedValue = formatNumber(cleanedData);

        // Update the input field with formatted value
        currentTarget.value = formattedValue;

        // Trigger the onChange event with the formatted value to ensure proper handling
        const changeEvent = new Event('change', { bubbles: true });
        Object.defineProperty(changeEvent, 'target', {
            writable: false,
            value: {
                ...currentTarget,
                value: formattedValue
            }
        });

        // Dispatch the change event to trigger the proper handlers
        currentTarget.dispatchEvent(changeEvent);

        // Set cursor position at the end
        setTimeout(() => {
            currentTarget.setSelectionRange(formattedValue.length, formattedValue.length);
        }, 0);
    };

    const handleInstallmentPaste = (installmentId, field) => (e) => {
        e.preventDefault();
        const pastedData = e.clipboardData.getData('text/plain');

        // Clean the pasted data - remove any non-numeric characters except decimal point
        let cleanedData = pastedData.replace(/[^0-9.]/g, '');

        // Ensure only one decimal point
        const decimalParts = cleanedData.split('.');
        if (decimalParts.length > 2) {
            cleanedData = decimalParts[0] + '.' + decimalParts.slice(1).join('');
        }

        // Limit decimal places to 2
        if (cleanedData.includes('.')) {
            const [integerPart, decimalPart] = cleanedData.split('.');
            cleanedData = integerPart + '.' + decimalPart.substring(0, 2);
        }

        // Skip if no valid data
        if (!cleanedData || cleanedData === '.') return;

        // Create a formatted version with commas for display
        const formattedValue = formatNumber(cleanedData);

        // Update the input field with formatted value
        e.target.value = formattedValue;

        // Call the installment change handler with the formatted value
        handleInstallmentChange(installmentId, field, formattedValue);

        // Set cursor position at the end
        setTimeout(() => {
            e.target.setSelectionRange(formattedValue.length, formattedValue.length);
        }, 0);
    };

    const addInstallment = () => {
        const newId = Math.max(...installments.map(inst => inst.id)) + 1;
        setInstallments(prev => {
            const updated = [...prev, { id: newId, amount: '', date: '', amount_conversion: '', status: 'unpaid' }];
            // Validate amounts after adding to update error state if needed
            setTimeout(() => validateInstallmentAmounts(true), 0);
            return updated;
        });
    };

    const deleteInstallment = (id) => {
        setInstallments(prev => {
            const filtered = prev.filter(inst => inst.id !== id);
            // Validate amounts after deletion to update error state
            setTimeout(() => validateInstallmentAmounts(true), 0);
            return filtered;
        });
    };

    const calculateTotalInstallmentAmount = () => {
        return installments.reduce((total, inst) => {
            const amount = parseFormattedNumber(inst.amount) || 0;
            return total + amount;
        }, 0);
    };

    const calculateTotalConversionAmount = () => {
        return installments.reduce((total, inst) => {
            const conversionAmount = parseFormattedNumber(inst.amount_conversion) || 0;
            return total + conversionAmount;
        }, 0);
    };

    const validateInstallmentAmounts = (updateErrorState = true) => {
        const totalInstallmentAmount = calculateTotalInstallmentAmount();
        const downPaymentAmount = parseFormattedNumber(downPayment) || 0;

        if (currency === 'EGP') {
            // For EGP: Allow installments to be between expected amount and expected amount + maintenance + utilities
            const expectedInstallmentTotal = totalPrice - downPaymentAmount;
            const maintenanceAmount = parseFormattedNumber(maintenanceValue) || 0;
            const utilitiesAmount = parseFormattedNumber(utilities) || 0;
            const maxAllowedTotal = expectedInstallmentTotal + maintenanceAmount + utilitiesAmount;

            if (totalInstallmentAmount < expectedInstallmentTotal || totalInstallmentAmount > maxAllowedTotal) {
                if (updateErrorState) {
                    if (totalInstallmentAmount < expectedInstallmentTotal) {
                        setInstallmentAmountError(`Total installment amount ($${totalInstallmentAmount.toLocaleString()}) is less than minimum required ($${expectedInstallmentTotal.toLocaleString()})`);
                    } else if (totalInstallmentAmount > maxAllowedTotal) {
                        setInstallmentAmountError(`Total installment amount ($${totalInstallmentAmount.toLocaleString()}) exceeds maximum allowed ($${maxAllowedTotal.toLocaleString()}) including maintenance and utilities`);
                    }
                }
                return false;
            }
        } else {
            // For non-EGP currencies: Allow conversion amounts to be between expected amount and expected amount + maintenance + utilities
            const targetTotalPrice = parseFormattedNumber(customTotalPrice) || 0;
            const totalConversionAmount = calculateTotalConversionAmount();
            const downPaymentConversionAmount = parseFormattedNumber(downPaymentConversion) || 0;
            const expectedConversionTotal = targetTotalPrice - downPaymentConversionAmount;
            const maintenanceAmount = parseFormattedNumber(maintenanceValue) || 0;
            const utilitiesAmount = parseFormattedNumber(utilities) || 0;
            const maxAllowedConversionTotal = expectedConversionTotal + maintenanceAmount + utilitiesAmount;

            // Total conversion amounts should be within the allowed range
            if (totalConversionAmount < expectedConversionTotal || totalConversionAmount > maxAllowedConversionTotal) {
                if (updateErrorState) {
                    if (totalConversionAmount < expectedConversionTotal) {
                        setInstallmentAmountError(`Total conversion amounts ($${totalConversionAmount.toLocaleString()}) is less than minimum required ($${expectedConversionTotal.toLocaleString()})`);
                    } else if (totalConversionAmount > maxAllowedConversionTotal) {
                        setInstallmentAmountError(`Total conversion amounts ($${totalConversionAmount.toLocaleString()}) exceeds maximum allowed ($${maxAllowedConversionTotal.toLocaleString()}) including maintenance and utilities`);
                    }
                }
                return false;
            }
        }

        if (updateErrorState) {
            setInstallmentAmountError('');
        }
        return true;
    };

    const hasValidInstallmentData = () => {
        if (paymentMethod !== 'installment') return true;
        // Check if all installment rows have both amount and date filled
        return installments.every(inst => inst.amount && inst.date);
    };

    const isFormComplete = (isFromEdit = false) => {
        if (!isFromEdit) {
            if (!(selectedCustomer || selectedCustomerData) || !paymentMethod) return false;
            // Check required new fields
            if (!clientInterests || !purposeOfPurchase || !currency) return false;
            // Check if maintenance value and utilities are provided
            if (!maintenanceValue || !utilities) return false;

            // Check if maintenance entries have valid data (at least one entry with value and date)
            const hasValidMaintenanceEntries = maintenanceEntries.some(entry => entry.value && entry.date);
            if (!hasValidMaintenanceEntries) return false;

            // Check if utilities entries have valid data (at least one entry with value and date)
            const hasValidUtilitiesEntries = utilitiesEntries.some(entry => entry.value && entry.date);
            if (!hasValidUtilitiesEntries) return false;

            // Check if contract date is provided
            { if (!contractDate) return false; }
        }
        // Check if currency is not EGP, then customTotalPrice is required
        if (currency !== 'EGP' && !customTotalPrice) return false;

        // For edit mode, also check maintenance and utilities entries
        if (isFromEdit) {
            const hasValidMaintenanceEntries = maintenanceEntries.some(entry => entry.value && entry.date);
            const hasValidUtilitiesEntries = utilitiesEntries.some(entry => entry.value && entry.date);
            if (!hasValidMaintenanceEntries || !hasValidUtilitiesEntries) return false;
        }

        if (paymentMethod === 'cash') return true;
        if (paymentMethod === 'installment') {
            const hasValidInstallments = installments.every(inst => inst.amount && inst.date);
            const hasValidYear = installmentYear;

            // Check EGP validation status using new range logic
            const expectedInstallments = totalPrice - (parseFormattedNumber(downPayment) || 0);
            const currentInstallments = calculateTotalInstallmentAmount();
            const maintenanceAmount = parseFormattedNumber(maintenanceValue) || 0;
            const utilitiesAmount = parseFormattedNumber(utilities) || 0;
            const maxAllowed = expectedInstallments + maintenanceAmount + utilitiesAmount;
            const isEgpValid = (currentInstallments >= expectedInstallments && currentInstallments <= maxAllowed);

            // Check non-EGP validation status if applicable
            let isNonEgpValid = true;
            if (currency !== 'EGP') {
                const targetTotalPrice = parseFormattedNumber(customTotalPrice) || 0;
                const totalConversionAmount = calculateTotalConversionAmount();
                const downPaymentConversionAmount = parseFormattedNumber(downPaymentConversion) || 0;
                const expectedConversionTotal = targetTotalPrice - downPaymentConversionAmount;
                const maintenanceAmount = parseFormattedNumber(maintenanceValue) || 0;
                const utilitiesAmount = parseFormattedNumber(utilities) || 0;
                const maxAllowedConversionTotal = expectedConversionTotal + maintenanceAmount + utilitiesAmount;
                isNonEgpValid = (totalConversionAmount >= expectedConversionTotal && totalConversionAmount <= maxAllowedConversionTotal);
            }

            // For EGP currency, only check EGP validation
            if (currency === 'EGP') {
                return hasValidInstallments && hasValidYear && isEgpValid;
            } else {
                // For non-EGP currencies, both validations must pass
                return hasValidInstallments && hasValidYear && isEgpValid && isNonEgpValid;
            }
        }
        return false;
    };

    const handleSave = async () => {
        if (isFormComplete()) {
            setSaveLoading(true);
            setSaveError('');

            try {
                const requestData = {
                    unit_id: unit?.id,
                    // customer_id: selectedCustomer,
                    swag_id: selectedCustomerData.swag_id,
                    payment_method: paymentMethod,
                    client_interests: clientInterests,
                    purpose_of_purchase: purposeOfPurchase,
                    down_payment: downPayment ? parseFormattedNumber(downPayment) : 0,
                    maintenance: maintenanceEntries
                        .filter(entry => entry.value && entry.date)
                        .map(entry => ({
                            amount: parseFormattedNumber(entry.value),
                            date: entry.date,
                            paid: entry.paid
                        })),
                    utilities: utilitiesEntries
                        .filter(entry => entry.value && entry.date)
                        .map(entry => ({
                            amount: parseFormattedNumber(entry.value),
                            date: entry.date,
                            paid: entry.paid
                        })),
                    currency: currency,
                    contract_date: contractDate
                };

                // Add down payment conversion if currency is not EGP
                if (currency !== 'EGP' && downPaymentConversion) {
                    requestData.down_payment_conversion = parseFormattedNumber(downPaymentConversion);
                } else if (currency === 'EGP' && downPayment) {
                    // If currency is EGP, use the EGP down payment value as conversion
                    requestData.down_payment_conversion = parseFormattedNumber(downPayment);
                }

                // Add custom total price if currency is not EGP
                if (currency !== 'EGP' && customTotalPrice) {
                    requestData.total_price = parseFormattedNumber(customTotalPrice);
                } else {
                    requestData.total_price = totalPrice;
                }

                if (paymentMethod === 'installment') {
                    requestData.installments = installments
                        .filter(inst => inst.amount && inst.date)
                        .map(inst => ({
                            amount: parseFormattedNumber(inst.amount),
                            date: inst.date,
                            amount_conversion: currency === 'EGP'
                                ? parseFormattedNumber(inst.amount)
                                : (inst.amount_conversion ? parseFormattedNumber(inst.amount_conversion) : 0),
                            status: inst.status || 'unpaid'
                        }));
                    requestData.installment_year = parseInt(installmentYear);
                }

                // Add client comments if in edit mode
                if (isInEditMode) {
                    requestData.client_comments = clientComments;
                }

                let response;
                if (isInEditMode && unitCustomerId) {
                    // Update existing customer-unit relationship
                    response = await updateCustomerUnitDataEdit(unitCustomerId, requestData);
                    dispatch(showSuccessNotification('Customer unit data updated successfully!'));
                } else {
                    // Create new customer-unit relationship
                    response = await addUnitCustomer(requestData);
                    dispatch(showSuccessNotification('Customer added to unit successfully!'));
                }

                console.log('API response:', response);


                if (onSave) {
                    // Call onSave with success flag to trigger unit list refresh
                    onSave({
                        // customerId: selectedCustomer,
                        paymentMethod,
                        totalPrice,
                        unitId: unit?.id,
                        installments: paymentMethod === 'installment' ? installments : null,
                        maintenanceEntries: maintenanceEntries.filter(entry => entry.value && entry.date),
                        utilitiesEntries: utilitiesEntries.filter(entry => entry.value && entry.date),
                        success: true
                    });
                } else {
                    // If no onSave callback, navigate to customer profile
                    // navigate(`/customer/${selectedCustomer}`);
                }
            } catch (error) {
                console.error('Failed to save customer unit data:', error);
                setSaveError(error.message || 'Failed to save customer unit data');
                dispatch(showErrorNotification(`Failed to save customer unit data: ${error.message || 'Unknown error'}`));
            } finally {
                setSaveLoading(false);
            }
        }
    };

    const handleEdit = async () => {
        if (isFormComplete(true) && unitCustomerId) {
            setEditLoading(true);
            setSaveError('');

            try {
                const requestData = {
                    unit_customer_id: unitCustomerId,
                    client_interests: clientInterests,
                    purpose_of_purchase: purposeOfPurchase,
                    client_comments: clientComments,
                    currency: currency,
                    maintenance: maintenanceEntries
                        .filter(entry => entry.value && entry.date)
                        .map(entry => ({
                            amount: parseFormattedNumber(entry.value),
                            date: entry.date,
                            paid: entry.paid
                        })),
                    utilities: utilitiesEntries
                        .filter(entry => entry.value && entry.date)
                        .map(entry => ({
                            amount: parseFormattedNumber(entry.value),
                            date: entry.date,
                            paid: entry.paid
                        })),
                    payment_method: paymentMethod,
                    contract_date: contractDate
                };

                // Add total price based on currency
                if (currency !== 'EGP' && customTotalPrice) {
                    requestData.total_price = parseFormattedNumber(customTotalPrice);
                } else {
                    requestData.total_price = totalPrice;
                }

                if (paymentMethod === 'installment') {
                    requestData.installment_year = installmentYear;
                    requestData.down_payment = downPayment ? parseFormattedNumber(downPayment) : 0;

                    // Add down payment conversion if currency is not EGP
                    if (currency !== 'EGP' && downPaymentConversion) {
                        requestData.down_payment_conversion = parseFormattedNumber(downPaymentConversion);
                    } else if (currency === 'EGP' && downPayment) {
                        requestData.down_payment_conversion = downPayment ? parseFormattedNumber(downPayment) : 0;

                    }

                    // Add installments data
                    requestData.installments = installments.map(inst => ({
                        amount: parseFormattedNumber(inst.amount) || 0,
                        amount_conversion: inst.amount_conversion ? parseFormattedNumber(inst.amount_conversion) : undefined,
                        date: inst.date,
                        status: inst.status || 'unpaid'
                    }));
                }

                console.log('Sending edit data to API:', requestData);

                const response = await updateCustomerUnitDataEdit(requestData);
                console.log('Edit API response:', response);

                dispatch(showSuccessNotification('Customer unit data updated successfully'));

                // Refresh the component data or navigate back
                if (onSave) {
                    onSave(response);
                } else {
                    // Navigate back one step
                    navigate(-1);
                }
            } catch (error) {
                console.error('Failed to update customer unit data:', error);
                setSaveError(error.message || 'Failed to update customer unit data');
                dispatch(showErrorNotification(`Failed to update customer unit data: ${error.message || 'Unknown error'}`));
            } finally {
                setEditLoading(false);
            }
        }
    };

    if (unitLoading) {
        return (
            <div className={styles.container}>
                <div className={styles.loadingState}>
                    <h3>Loading unit details...</h3>
                </div>
            </div>
        );
    }

    if (unitId && !unit) {
        return (
            <div className={styles.container}>
                <div className={styles.errorState}>
                    <h3>Unit not found</h3>
                    <p>The unit could not be loaded.</p>
                    <button
                        className={styles.backButton}
                        onClick={onCancel || (() => navigate(-1))}
                    >
                        Back
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className={styles.container}>
            <div className={styles.content}>
                <div className={styles.leftContent}>
                    <div className={styles.backSection}>
                        <button
                            className={styles.backButton}
                            onClick={onCancel || (() => navigate(-1))}
                        >
                            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                <path d="M19 12H5" />
                                <path d="M12 19l-7-7 7-7" />
                            </svg>
                            Back
                        </button>
                    </div>

                    <div className={styles.paymentContainer}>
                        <div className={styles.cashAmount}>
                            <label>Cash Amount:</label>
                            <span className={styles.amountValue}>${totalPrice.toLocaleString()}</span>
                        </div>
                        <div className={styles.paymentMethod}>
                            <label>Payment Method:</label>
                            <div className={styles.paymentOptions}>
                                <label className={styles.radioOption}>
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        value="installment"
                                        checked={paymentMethod === 'installment'}
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                        disabled={isInEditMode}
                                    />
                                    <span>Installment</span>
                                </label>
                                <label className={styles.radioOption}>
                                    <input
                                        type="radio"
                                        name="paymentMethod"
                                        value="cash"
                                        checked={paymentMethod === 'cash'}
                                        onChange={(e) => setPaymentMethod(e.target.value)}
                                        disabled={isInEditMode}
                                    />
                                    <span>Cash</span>
                                </label>
                            </div>
                        </div>
                    </div>


                    <div className={styles.customerSection}>
                        <label>Customer:</label>
                        {isInEditMode ? (
                            <div className={styles.customerDisplayOnly}>
                                <span className={styles.customerUserNameValue}>
                                    {customerFromEdit || 'No username'}
                                </span>
                            </div>
                        ) : (
                            <div className={styles.dropdownContainer} ref={dropdownRef}>
                                <div
                                    className={styles.dropdownTrigger}
                                    onClick={() => setIsDropdownOpen(!isDropdownOpen)}
                                >
                                    <div className={`${styles.selectedValue} ${!selectedCustomerData ? styles.empty : ''}`}>
                                        {selectedCustomerData ? (
                                            <>
                                                <div className={styles.customerAvatar}>
                                                    {selectedCustomerData.name.charAt(0).toUpperCase()}
                                                </div>
                                                <div className={styles.customerName}>
                                                    {selectedCustomerData.name}
                                                </div>
                                            </>
                                        ) : (
                                            'Select customer...'
                                        )}
                                    </div>
                                    <svg
                                        width="16"
                                        height="16"
                                        viewBox="0 0 24 24"
                                        fill="none"
                                        stroke="currentColor"
                                        strokeWidth="2"
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        style={{
                                            transform: isDropdownOpen ? 'rotate(180deg)' : 'rotate(0deg)',
                                            transition: 'transform 0.3s ease'
                                        }}
                                    >
                                        <path d="m6 9 6 6 6-6" />
                                    </svg>
                                </div>

                                {isDropdownOpen && (
                                    <div className={styles.dropdownMenu}>
                                        <div className={styles.searchContainer}>
                                            <input
                                                type="text"
                                                placeholder="Search customers by SWAG ID..."
                                                value={searchQuery}
                                                onChange={(e) => setSearchQuery(e.target.value)}
                                                className={styles.searchInput}
                                                autoFocus
                                            />
                                        </div>
                                        <div className={styles.optionsList}>
                                            {loading ? (
                                                <div className={styles.loadingMessage}>
                                                    Searching...
                                                </div>
                                            ) : customers.length > 0 ? (
                                                customers.map(customer => (
                                                    <div
                                                        key={customer.swag_id}
                                                        className={styles.option}
                                                        onClick={() => handleCustomerSelect(customer.swag_id,customer.name)}
                                                    >
                                                        <div className={styles.customerAvatar}>
                                                            {customer.name.charAt(0).toUpperCase()}
                                                        </div>
                                                        <div className={styles.customerInfo}>
                                                            <div className={styles.customerName}>
                                                                {customer.name}
                                                            </div>
                                                            <div className={styles.customerEmail}>
                                                                {customer.email ?? customer.type}
                                                            </div>
                                                        </div>
                                                    </div>
                                                ))
                                            ) : searchQuery.trim().length > 0 ? (
                                                <div className={styles.noResults}>
                                                    No customers found with this SWAG ID
                                                </div>
                                            ) : (
                                                <div className={styles.noResults}>
                                                    Start typing SWAG ID to search customers
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                )}
                            </div>
                        )}
                    </div>

                    <div className={styles.clientInterestsSection}>
                        <label>Client Interests:</label>
                        <select
                            value={clientInterests}
                            onChange={(e) => setClientInterests(e.target.value)}
                            className={styles.selectField}
                        >
                            <option value="Residential">Residential</option>
                            <option value="Commercial">Commercial</option>
                            <option value="Summer">Summer</option>
                        </select>
                    </div>

                    <div className={styles.purposeSection}>
                        <label>Purpose of Purchase:</label>
                        <select
                            value={purposeOfPurchase}
                            onChange={(e) => setPurposeOfPurchase(e.target.value)}
                            className={styles.selectField}
                        >
                            <option value="Investment">Investment</option>
                            <option value="Personal use">Personal use</option>
                        </select>
                    </div>

                    <div className={styles.purposeSection}>
                        <label>Contract Date:</label>
                        <input
                            type="date"
                            value={contractDate}
                            onChange={(e) => setContractDate(e.target.value)}
                            className={styles.inputField}
                            required
                        />
                    </div>

                    {(isInEditMode && clientComments) && (
                        <div className={styles.purposeSection}>
                            <label>Client Comments:</label>
                            <textarea
                                value={clientComments}
                                onChange={(e) => setClientComments(e.target.value)}
                                className={styles.textareaField}
                                placeholder="Enter client comments..."
                                rows="3"
                            />
                        </div>
                    )}


                    <div className={styles.currencySection}>
                        <label>Currency:</label>
                        <select
                            value={currency}
                            onChange={(e) => {
                                setCurrency(e.target.value);
                                // Clear validation error and revalidate when currency changes
                                setInstallmentAmountError('');
                                setTimeout(() => validateInstallmentAmounts(true), 0);
                            }}
                            className={styles.selectField}
                        >
                            <option value="EGP">EGP</option>
                            <option value="USD">USD</option>
                            <option value="EUR">EUR</option>
                            <option value="SAR">SAR</option>
                            <option value="AED">AED</option>
                        </select>
                    </div>

                    <div className={styles.maintenanceSection}>
                        <label>Maintenance Values:</label>
                        <div className={styles.simpleEntriesContainer}>
                            {maintenanceEntries.map((entry, index) => (
                                <div key={entry.id} className={styles.simpleEntryRow}>
                                    <input
                                        type="text"
                                        placeholder="Enter amount"
                                        value={formatNumber(entry.value)}
                                        onChange={(e) => handleMaintenanceEntryChange(entry.id, 'value', e.target.value)}
                                        className={styles.simpleValueInput}
                                        onKeyDown={handleAmountKeyPress}
                                        onPaste={(e) => {
                                            e.preventDefault();
                                            const pastedData = e.clipboardData.getData('text/plain');
                                            let cleanedData = pastedData.replace(/[^0-9.]/g, '');
                                            const decimalParts = cleanedData.split('.');
                                            if (decimalParts.length > 2) {
                                                cleanedData = decimalParts[0] + '.' + decimalParts.slice(1).join('');
                                            }
                                            if (cleanedData.includes('.')) {
                                                const [integerPart, decimalPart] = cleanedData.split('.');
                                                cleanedData = integerPart + '.' + decimalPart.substring(0, 2);
                                            }
                                            if (cleanedData && cleanedData !== '.') {
                                                const formattedValue = formatNumber(cleanedData);
                                                handleMaintenanceEntryChange(entry.id, 'value', formattedValue);
                                            }
                                        }}
                                    />
                                    <input
                                        type="date"
                                        value={entry.date}
                                        onChange={(e) => handleMaintenanceEntryChange(entry.id, 'date', e.target.value)}
                                        className={styles.simpleDateInput}
                                    />
                                    <div className={styles.statusField}>
                                        <select
                                            value={entry.paid ? 'paid' : 'unpaid'}
                                            onChange={(e) => handleMaintenanceEntryChange(entry.id, 'paid', e.target.value === 'paid')}
                                            className={styles.statusSelect}
                                        >
                                            <option value="unpaid">Unpaid</option>
                                            <option value="paid">Paid</option>
                                        </select>
                                    </div>
                                    {maintenanceEntries.length > 1 && (
                                        <button
                                            className={styles.simpleDeleteButton}
                                            onClick={() => deleteMaintenanceEntry(entry.id)}
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button className={styles.simplePlusButton} onClick={addMaintenanceEntry}>
                                +
                            </button>
                        </div>
                        <div className={styles.simpleTotalDisplay}>
                            Total: {maintenanceValueDisplay || '0'}
                        </div>
                    </div>

                    <div className={styles.utilitiesSection}>
                        <label>Utilities Values:</label>
                        <div className={styles.simpleEntriesContainer}>
                            {utilitiesEntries.map((entry, index) => (
                                <div key={entry.id} className={styles.simpleEntryRow}>
                                    <input
                                        type="text"
                                        placeholder="Enter amount"
                                        value={formatNumber(entry.value)}
                                        onChange={(e) => handleUtilitiesEntryChange(entry.id, 'value', e.target.value)}
                                        className={styles.simpleValueInput}
                                        onKeyDown={handleAmountKeyPress}
                                        onPaste={(e) => {
                                            e.preventDefault();
                                            const pastedData = e.clipboardData.getData('text/plain');
                                            let cleanedData = pastedData.replace(/[^0-9.]/g, '');
                                            const decimalParts = cleanedData.split('.');
                                            if (decimalParts.length > 2) {
                                                cleanedData = decimalParts[0] + '.' + decimalParts.slice(1).join('');
                                            }
                                            if (cleanedData.includes('.')) {
                                                const [integerPart, decimalPart] = cleanedData.split('.');
                                                cleanedData = integerPart + '.' + decimalPart.substring(0, 2);
                                            }
                                            if (cleanedData && cleanedData !== '.') {
                                                const formattedValue = formatNumber(cleanedData);
                                                handleUtilitiesEntryChange(entry.id, 'value', formattedValue);
                                            }
                                        }}
                                    />
                                    <input
                                        type="date"
                                        value={entry.date}
                                        onChange={(e) => handleUtilitiesEntryChange(entry.id, 'date', e.target.value)}
                                        className={styles.simpleDateInput}
                                    />
                                    <div className={styles.statusField}>
                                        <select
                                            value={entry.paid ? 'paid' : 'unpaid'}
                                            onChange={(e) => handleUtilitiesEntryChange(entry.id, 'paid', e.target.value === 'paid')}
                                            className={styles.statusSelect}
                                        >
                                            <option value="unpaid">Unpaid</option>
                                            <option value="paid">Paid</option>
                                        </select>
                                    </div>
                                    {utilitiesEntries.length > 1 && (
                                        <button
                                            className={styles.simpleDeleteButton}
                                            onClick={() => deleteUtilitiesEntry(entry.id)}
                                        >
                                            ×
                                        </button>
                                    )}
                                </div>
                            ))}
                            <button className={styles.simplePlusButton} onClick={addUtilitiesEntry}>
                                +
                            </button>
                        </div>
                        <div className={styles.simpleTotalDisplay}>
                            Total: {utilitiesDisplay || '0'}
                        </div>
                    </div>


                    {paymentMethod === 'installment' && (
                        <div className={styles.downPaymentSection}>
                            <label>Down Payment: (in EGP)</label>
                            <input
                                type="text"
                                placeholder="Enter down payment amount (e.g., 10,000.50)"
                                value={downPaymentDisplay}
                                onChange={(e) => handleFormattedNumericChange('downPayment', e.target.value, setDownPaymentDisplay)}
                                className={styles.inputField}
                                onKeyDown={handleAmountKeyPress}
                                onPaste={handleAmountPaste}
                            />
                        </div>
                    )
                    }

                    {(currency !== 'EGP' && paymentMethod === 'installment') && (
                        <div className={styles.downPaymentConversionSection}>
                            <label>Down Payment Conversion: in {currency}</label>
                            <input
                                type="text"
                                placeholder="Enter down payment conversion amount (e.g., 2,500.75)"
                                value={downPaymentConversionDisplay}
                                onChange={(e) => handleFormattedNumericChange('downPaymentConversion', e.target.value, setDownPaymentConversionDisplay)}
                                className={styles.inputField}
                                onKeyDown={handleAmountKeyPress}
                                onPaste={handleAmountPaste}
                            />
                        </div>
                    )}


                    {currency !== 'EGP' && (
                        <div className={styles.customTotalPriceSection}>
                            <label>Total Price: in {currency}</label>
                            <input
                                type="text"
                                placeholder="Enter total price (e.g., 250,000.00)"
                                value={customTotalPriceDisplay}
                                onChange={(e) => handleFormattedNumericChange('customTotalPrice', e.target.value, setCustomTotalPriceDisplay)}
                                className={styles.inputField}
                                onKeyDown={handleAmountKeyPress}
                                onPaste={handleAmountPaste}
                            />
                        </div>
                    )}


                    {paymentMethod === 'installment' && (
                        <div className={styles.installmentYearSection}>
                            <label>Installment Year:</label>
                            <input
                                type="number"
                                placeholder="Enter installment year"
                                value={installmentYear}
                                onChange={(e) => setInstallmentYear(e.target.value)}
                                className={styles.installmentYearInput}
                                min="1"
                                step="1"
                            />
                        </div>
                    )}

                    {paymentMethod === 'installment' && (
                        <div className={styles.totalAmountSection}>
                            <div className={styles.validationTable}>
                                <table className={styles.amountTable}>
                                    <thead>
                                        <tr>
                                            <th className={styles.currencyColumn}>Currency</th>
                                            <th className={styles.priceColumn}>Total Price</th>
                                            <th className={styles.paymentColumn}>Down Payment</th>
                                            <th className={styles.expectedColumn}>Expected Installments</th>
                                            <th className={styles.currentColumn}>Current Installments</th>
                                            <th className={styles.statusColumn}>Status</th>
                                        </tr>
                                    </thead>
                                    <tbody>
                                        {/* EGP Row - Always displayed and validated */}
                                        <tr className={styles.validationRow}>
                                            <td className={styles.currencyCell}>
                                                <strong>EGP</strong>
                                                {currency === 'EGP' && <span className={styles.primaryBadge}>Primary</span>}
                                            </td>
                                            <td className={styles.priceCell}>{totalPrice.toLocaleString()}</td>
                                            <td className={styles.paymentCell}>{downPayment ? parseFormattedNumber(downPayment).toLocaleString() : '0'}</td>
                                            <td className={styles.expectedCell}>{(totalPrice - (parseFormattedNumber(downPayment) || 0)).toLocaleString()}</td>
                                            <td className={`${styles.currentCell} ${(() => {
                                                const expectedInstallments = totalPrice - (parseFormattedNumber(downPayment) || 0);
                                                const currentInstallments = calculateTotalInstallmentAmount();
                                                const maintenance = parseFormattedNumber(maintenanceValue) || 0;
                                                const utilitiesAmount = parseFormattedNumber(utilities) || 0;
                                                const maxAllowed = expectedInstallments + maintenance + utilitiesAmount;
                                                return (currentInstallments < expectedInstallments || currentInstallments > maxAllowed) ? styles.errorCell : '';
                                            })()}`}>
                                                {calculateTotalInstallmentAmount().toLocaleString()}
                                            </td>
                                            <td className={styles.statusCell}>
                                                {(() => {
                                                    const expectedInstallments = totalPrice - (parseFormattedNumber(downPayment) || 0);
                                                    const currentInstallments = calculateTotalInstallmentAmount();
                                                    const maintenance = parseFormattedNumber(maintenanceValue) || 0;
                                                    const utilitiesAmount = parseFormattedNumber(utilities) || 0;
                                                    const maxAllowed = expectedInstallments + maintenance + utilitiesAmount;
                                                    return (currentInstallments >= expectedInstallments && currentInstallments <= maxAllowed) ?
                                                        <span className={styles.validStatus}>✓ Valid</span> :
                                                        <span className={styles.errorStatus}>✗ Invalid</span>;
                                                })()}
                                            </td>
                                        </tr>
                                        {/* Non-EGP Currency Row - Only displayed when currency is not EGP */}
                                        {currency !== 'EGP' && (
                                            <tr className={styles.validationRow}>
                                                <td className={styles.currencyCell}>
                                                    <strong>{currency}</strong>
                                                    <span className={styles.primaryBadge}>Primary</span>
                                                </td>
                                                <td className={styles.priceCell}>{customTotalPrice ? parseFormattedNumber(customTotalPrice).toLocaleString() : '0'}</td>
                                                <td className={styles.paymentCell}>{downPaymentConversion ? parseFormattedNumber(downPaymentConversion).toLocaleString() : '0'}</td>
                                                <td className={styles.expectedCell}>{customTotalPrice ? (parseFormattedNumber(customTotalPrice) - (parseFormattedNumber(downPaymentConversion) || 0)).toLocaleString() : '0'}</td>
                                                <td className={`${styles.currentCell} ${(() => {
                                                    const expectedInstallments = parseFormattedNumber(customTotalPrice) - (parseFormattedNumber(downPaymentConversion) || 0);
                                                    const currentInstallments = calculateTotalConversionAmount();
                                                    const maintenance = parseFormattedNumber(maintenanceValue) || 0;
                                                    const utilitiesAmount = parseFormattedNumber(utilities) || 0;
                                                    const maxAllowed = expectedInstallments + maintenance + utilitiesAmount;
                                                    return (currentInstallments < expectedInstallments || currentInstallments > maxAllowed) ? styles.errorCell : '';
                                                })()}`}>
                                                    {calculateTotalConversionAmount().toLocaleString()}
                                                </td>
                                                <td className={styles.statusCell}>
                                                    {(() => {
                                                        const expectedInstallments = parseFormattedNumber(customTotalPrice) - (parseFormattedNumber(downPaymentConversion) || 0);
                                                        const currentInstallments = calculateTotalConversionAmount();
                                                        const maintenance = parseFormattedNumber(maintenanceValue) || 0;
                                                        const utilitiesAmount = parseFormattedNumber(utilities) || 0;
                                                        const maxAllowed = expectedInstallments + maintenance + utilitiesAmount;
                                                        return (currentInstallments >= expectedInstallments && currentInstallments <= maxAllowed) ?
                                                            <span className={styles.validStatus}>✓ Valid</span> :
                                                            <span className={styles.errorStatus}>✗ Invalid</span>;
                                                    })()}
                                                </td>
                                            </tr>
                                        )}
                                    </tbody>
                                </table>
                            </div>
                        </div>
                    )}

                    {paymentMethod === 'installment' && (
                        <div className={styles.installmentSection}>
                            <label>Installments:</label>
                            <div className={styles.installmentRows}>
                                {installments.map((installment, index) => (
                                    <div key={installment.id} className={styles.installmentRow}>
                                        <div className={styles.installmentIndex}>{index + 1}</div>
                                        <div className={styles.installmentField}>
                                            <label>Amount:</label>
                                            <input
                                                type="text"
                                                placeholder="Enter amount (e.g., 5,000.00)"
                                                value={formatNumber(installment.amount)}
                                                onChange={(e) => handleInstallmentChange(installment.id, 'amount', e.target.value)}
                                                className={styles.installmentInput}
                                                onKeyDown={handleAmountKeyPress}
                                                onPaste={handleInstallmentPaste(installment.id, 'amount')}
                                            />
                                        </div>
                                        {currency !== 'EGP' && (
                                            <div className={styles.installmentField}>
                                                <label>Amount Conversion: in {currency}</label>
                                                <input
                                                    type="text"
                                                    placeholder="Enter conversion amount (e.g., 1,250.50)"
                                                    value={formatNumber(installment.amount_conversion)}
                                                    onChange={(e) => handleInstallmentChange(installment.id, 'amount_conversion', e.target.value)}
                                                    className={styles.installmentInput}
                                                    onKeyDown={handleAmountKeyPress}
                                                    onPaste={handleInstallmentPaste(installment.id, 'amount_conversion')}
                                                />
                                            </div>
                                        )}
                                        <div className={styles.installmentField}>
                                            <label>Date:</label>
                                            <input
                                                type="date"
                                                value={installment.date}
                                                onChange={(e) => handleInstallmentChange(installment.id, 'date', e.target.value)}
                                                className={styles.installmentInput}
                                            />
                                        </div>
                                        {isInEditMode && (
                                            <div className={styles.installmentField}>
                                                <label>Status:</label>
                                                <select
                                                    value={installment.status}
                                                    onChange={(e) => handleInstallmentChange(installment.id, 'status', e.target.value)}
                                                    className={styles.installmentSelect}
                                                >
                                                    <option value="unpaid">Unpaid</option>
                                                    <option value="paid">Paid</option>
                                                </select>
                                            </div>
                                        )}

                                        {installments.length > 1 && (
                                            <button
                                                className={styles.deleteInstallmentButton}
                                                onClick={() => deleteInstallment(installment.id)}
                                            >
                                                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                                                    <path d="M18 6L6 18M6 6l12 12" />
                                                </svg>
                                            </button>
                                        )}
                                    </div>
                                ))}
                                <button className={styles.addInstallmentButton} onClick={addInstallment}>
                                    + Add Installment
                                </button>
                            </div>
                        </div>
                    )}

                    {/* Installment Amount Validation Error */}
                    {/* {installmentAmountError && (
                        <div className={styles.validationError}>
                            <p>{installmentAmountError}</p>
                        </div>
                    )} */}

                    {/* Error Message */}
                    {saveError && (
                        <div className={styles.errorMessage}>
                            <p>{saveError}</p>
                            <button
                                className={styles.dismissError}
                                onClick={() => setSaveError('')}
                            >
                                Dismiss
                            </button>
                        </div>
                    )}

                    <div className={styles.actions}>
                        {isInEditMode && isFormComplete(true) && (
                            <button
                                className={`${styles.editButton} ${editLoading ? styles.disabled : ''}`}
                                onClick={handleEdit}
                                disabled={editLoading}
                            >
                                {editLoading ? (
                                    <>
                                        <svg className={styles.spinner} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 12a9 9 0 11-6.219-8.56" />
                                        </svg>
                                        Updating...
                                    </>
                                ) : (
                                    'Edit Data'
                                )}
                            </button>
                        )}
                        {!isInEditMode && (selectedCustomer || selectedCustomerData) && paymentMethod && isFormComplete() && (
                            <button
                                className={`${styles.saveButton} ${saveLoading ? styles.disabled : ''}`}
                                onClick={handleSave}
                                disabled={saveLoading}
                            >
                                {saveLoading ? (
                                    <>
                                        <svg className={styles.spinner} width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                                            <path d="M21 12a9 9 0 11-6.219-8.56" />
                                        </svg>
                                        Saving...
                                    </>
                                ) : (
                                    'Save'
                                )}
                            </button>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default AddCustomerToUnit; 